import { Link } from "@inertiajs/react"
import { Activity } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { EmptyState } from "@/components/empty-state"
import { StatusIcon, type StatusTone } from "@/components/status-icon"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { pluralize, timeAgo } from "@/lib/format"
import { requestJson } from "@/lib/http"
import { subscribe } from "@/lib/realtime"
import { frontActivities, frontActivitiesDismissals } from "@/routes"
import type { ActivityEntry, ActivityFeed } from "@/types"

const STATE_TONES: Record<ActivityEntry["state"], StatusTone> = {
  attention: "warning",
  processing: "processing",
  closed: "neutral",
  done: "success",
}

function ActivityEntryItem({ entry, onDismiss }: { entry: ActivityEntry; onDismiss: (entry: ActivityEntry) => void }) {
  const failure = entry.bank_sync_failure

  return (
    <li className="px-5 py-4">
      <div className="flex items-start gap-3">
        <StatusIcon tone={STATE_TONES[entry.state]} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{entry.title}</p>
          {entry.description && <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>}
          {failure && (
            <>
              <p className="mt-1 text-sm text-muted-foreground">{failure.bank_account_name}</p>
              <p className="mt-1 text-xs text-subtle-foreground">
                Failed {pluralize(failure.failure_count, "time")} ·{" "}
                {failure.last_success_at
                  ? `Last successful sync ${timeAgo(failure.last_success_at)}`
                  : "No successful sync yet"}
              </p>
            </>
          )}
          {entry.closed_reason && (
            <p className="mt-1 text-xs text-subtle-foreground">
              {entry.closed_reason === "resolved" ? "Resolved by a later successful sync" : "Dismissed"}
            </p>
          )}
          {entry.completed_at && <p className="mt-1 text-xs text-subtle-foreground">{timeAgo(entry.completed_at)}</p>}
          {entry.link && (
            <Link href={entry.link.path} className="mt-2 inline-block text-xs font-medium text-primary hover:underline">
              {entry.link.label}
            </Link>
          )}
          {entry.state === "attention" && (
            <Button variant="link" size="inline" onClick={() => onDismiss(entry)} className="mt-2 flex text-xs text-muted-foreground">
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}

function ActivityGroup({
  title,
  entries,
  onDismiss,
}: {
  title: string
  entries: ActivityEntry[]
  onDismiss: (entry: ActivityEntry) => void
}) {
  if (entries.length === 0) return null

  return (
    <>
      <h3 className="bg-surface-subtle px-5 py-2 text-xs font-semibold tracking-wide text-subtle-foreground uppercase">
        {title} · {entries.length}
      </h3>
      <ul className="divide-y divide-border">
        {entries.map((entry) => (
          <ActivityEntryItem key={entry.id} entry={entry} onDismiss={onDismiss} />
        ))}
      </ul>
    </>
  )
}

/** Navbar button and popover listing background work: running, failed and recently finished. */
export function ActivityPanel() {
  const [feed, setFeed] = useState<ActivityFeed | null>(null)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const request = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller

    try {
      const data = await requestJson<{ feed: ActivityFeed }>(frontActivities.index(), { signal: controller.signal })
      setFeed(data.feed)
      setFailed(false)
    } catch (error) {
      if (controller.signal.aborted) return
      setFailed(true)
      console.error("Could not refresh activity", error)
    }
  }, [])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    // Several events often arrive together; one refresh covers them.
    const scheduleRefresh = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => void refresh(), 150)
    }

    void refresh()
    const unsubscribe = subscribe(
      "AccountChannel",
      (event) => {
        if (event.type === "activity.changed") scheduleRefresh()
      },
      scheduleRefresh,
    )
    window.addEventListener("focus", scheduleRefresh)
    // Leaving for a full page load cancels the request without aborting it; that is not a failure.
    const abortRequest = () => request.current?.abort()
    window.addEventListener("pagehide", abortRequest)
    // Back/forward cache restores the page without its aborted refresh, and without a guaranteed focus event.
    const refreshOnRestore = (event: PageTransitionEvent) => {
      if (event.persisted) scheduleRefresh()
    }
    window.addEventListener("pageshow", refreshOnRestore)

    return () => {
      clearTimeout(timeout)
      unsubscribe()
      window.removeEventListener("focus", scheduleRefresh)
      window.removeEventListener("pagehide", abortRequest)
      window.removeEventListener("pageshow", refreshOnRestore)
      request.current?.abort()
    }
  }, [refresh])

  const dismiss = async (entry: ActivityEntry) => {
    await requestJson(frontActivitiesDismissals.create(entry.id))
    await refresh()
  }

  const processingCount = feed?.processing.length ?? 0
  const attentionCount = feed?.needs_attention.length ?? 0
  const empty = feed !== null && processingCount + attentionCount + feed.recent.length === 0

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) void refresh()
      }}
    >
      <PopoverTrigger
        aria-label={`Activity: ${processingCount} processing, ${attentionCount} need attention`}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-inverse-muted-foreground outline-none hover:bg-inverse-hover hover:text-inverse-foreground focus-visible:ring-2 focus-visible:ring-inverse-foreground"
      >
        <Activity className="size-5" aria-hidden="true" />
        <span className="hidden sm:inline" role="status">
          {feed?.summary ?? "Activity"}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-[calc(100dvh-6rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden p-0 sm:w-96"
      >
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold">Activity</h2>
          <p className="text-xs text-subtle-foreground">Background updates</p>
        </div>
        {failed && (
          <p className="px-5 py-2 text-xs text-warning-strong" role="status">
            Updates could not be loaded.{" "}
            <Button variant="link" size="inline" className="text-xs text-warning-strong underline" onClick={() => void refresh()}>
              Try again
            </Button>
          </p>
        )}
        <div className="overflow-y-auto overscroll-contain">
          {empty ? (
            <EmptyState title="You're up to date" className="m-5">
              Background updates will appear here.
            </EmptyState>
          ) : (
            feed && (
              <>
                <ActivityGroup title="In progress" entries={feed.processing} onDismiss={dismiss} />
                <ActivityGroup title="Needs attention" entries={feed.needs_attention} onDismiss={dismiss} />
                <ActivityGroup title="Recent" entries={feed.recent} onDismiss={dismiss} />
                <p className="px-5 py-3 text-xs text-subtle-foreground">
                  Recent: latest 30 completed, resolved, or dismissed updates from the last 30 days.
                </p>
              </>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
